import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '../../lib/utils';

export type BadgeVariant = 'success' | 'warning' | 'info' | 'error' | 'default';

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-primary/10 text-primary',
  error: 'bg-error/10 text-error',
  default: 'bg-bg-muted text-text-muted',
};

// A bordered pill sits on busier rows (the email and activity lists), where a filled-only badge loses its edge.
const borderedStyles: Record<BadgeVariant, string> = {
  success: 'border border-success/40 bg-success/10 text-success',
  warning: 'border border-warning/40 bg-warning/10 text-warning',
  info: 'border border-primary/40 bg-primary/10 text-primary',
  error: 'border border-error/40 bg-error/10 text-error',
  default: 'border border-border bg-bg-subtle text-text-muted',
};

/** A badge's look, for an element that must be something other than a `<span>`, such as a status pill that links to its record. */
export function badgeClassName({ variant = 'default', bordered = false }: { variant?: BadgeVariant; bordered?: boolean } = {}): string {
  return cn(
    'inline-flex items-center rounded-full text-xs font-medium',
    bordered ? cn('whitespace-nowrap px-2.5 py-1', borderedStyles[variant]) : cn('px-2 py-0.5', variantStyles[variant]),
  );
}

type BadgeProps = ComponentPropsWithoutRef<'span'> & {
  variant?: BadgeVariant;
  bordered?: boolean;
};

export function Badge({ variant = 'default', bordered = false, children, className, ...props }: BadgeProps) {
  return (
    <span {...props} className={cn(badgeClassName({ variant, bordered }), className)}>
      {children}
    </span>
  );
}
